/**
 * FILE: apps/admin/src/lib/configDocs.ts
 * PURPOSE: The single source of truth for "what does this configuration knob
 *          actually do?" — every entry here drives both:
 *
 *            1. The in-app `<ConfigHelp helpId="…">` popovers next to each
 *               control across the admin's 14 pages.
 *            2. The auto-generated living reference at
 *               `docs/CONFIG_REFERENCE.md`, regenerated by
 *               `scripts/generate-config-reference.mjs` on every commit.
 *
 *          Why a typed dictionary, and not JSDoc / inline strings?
 *
 *            • DRIFT GUARD. `scripts/check-config-docs.mjs` regenerates the
 *              markdown into a temp buffer and diffs it against the committed
 *              file; if they disagree, the pre-commit hook fails. Inline
 *              strings have no equivalent enforcement.
 *
 *            • BACKEND GROUNDING. The unit tests in `configDocs.test.ts`
 *              cross-check every entry whose `backend.endpoint` is the shared
 *              settings PATCH against the allowlist regex-extracted from
 *              `packages/server/supabase/functions/api/index.ts:3096-3099`.
 *              If a backend column gets renamed without updating both ends,
 *              the test fails and tells you exactly which entry is stale.
 *              This is the mechanism that prevents this file from becoming
 *              a beautifully written lie six months from now.
 *
 *            • ONE LOCATION TO REVIEW. Non-engineering staff (support, ops)
 *              can review every user-facing explanation in one place, in
 *              plain TypeScript, without spelunking through 14 components.
 *
 *          ID convention: `route.section.field` — kebab/snake within each
 *          segment, dots between segments. Stable across visual rewrites of
 *          the underlying control. The route segment matches the URL path
 *          (e.g. `settings`, `integrations`, `compliance`); for primitives
 *          shared across pages (the SDK install card, mint-key flows), the
 *          segment is the component family name.
 *
 *          Style guide for entries:
 *
 *            • `summary` — one sentence, no jargon, no acronyms unrolled in
 *               place ("HTTP basic auth header" → "auth header"). Renders in
 *               the hover preview tooltip and as the first line of the popover.
 *            • `howItWorks` — 2-3 sentences. Talk about the data flow, not
 *               just the field shape. "When the value goes up X, behaviour
 *               Y becomes Z." Avoid implementation details unless they
 *               surface in the UI elsewhere.
 *            • `default.value` — exactly what the seed migration / fallback
 *               in code uses. Verified against the read-side edge function,
 *               not assumed.
 *            • `backend` — table+column where the value is persisted, the
 *               admin endpoint that writes it, and the named edge functions
 *               that READ it (the user-facing impact lives there). Skip the
 *               object entirely for purely client-side knobs (SDK preview
 *               toggles, debug-mode), so the popover hides "Where it lives".
 *            • `whenToChange` — heuristic. "Raise this when X happens; lower
 *               it when Y." If the answer is "never touch unless support
 *               asks", say that — being honest beats inventing a use case.
 *            • `learnMore` — point at a heading in this repo's README or
 *               docs/ where the mechanic is explained in more depth. Tested
 *               in `configDocs.test.ts` for `README.md` anchors.
 */

export interface ConfigDoc {
  /** Stable id used by `<ConfigHelp helpId="…">`. Format:
   *  `^[a-z][a-z0-9-]*(\.[a-z][a-z0-9_-]*)+$`. */
  id: string;
  /** The visible UI label this entry documents. Kept in the dictionary so
   *  the popover header reads identically to the field, even when the
   *  surrounding form composes its own label decoration. */
  label: string;
  /** One-sentence plain-English summary. Used as the hover preview tooltip
   *  on the trigger and as the first section of the popover. */
  summary: string;
  /** Two-three sentences on how the setting affects behaviour. */
  howItWorks: string;
  /** Default value the system uses when the field is empty/unset, plus an
   *  optional `range` for sliders / numeric inputs. */
  default: { value: string; range?: string };
  /** Backend lineage. Omit entirely for client-only knobs. */
  backend?: {
    /** Postgres table the value lives in. */
    table?: string;
    /** Column inside `table`. For composite values use a slash-separated
     *  list (e.g. `'allowed_domains / max_pages_per_call'`) — the
     *  generated markdown renders it verbatim. */
    column?: string;
    /** Admin REST endpoint that writes the value. */
    endpoint?: string;
    /** Edge functions / consumers that READ the value. */
    readBy?: string[];
  };
  /** Heuristic — when to nudge it up vs down. */
  whenToChange: string;
  /** Optional deep-link to a longer explanation. Same-origin anchors
   *  (`#configuration-reference-living`) are checked at test time. */
  learnMore?: { label: string; href: string };
}

/* ============================================================================
 *  THE DICTIONARY
 * ============================================================================
 *
 *  Order matters: the generator emits one markdown section per `route` group
 *  in declaration order. Within a route, declaration order is the order
 *  things appear in the UI. Keep this in sync when shuffling fields around.
 */

const SETTINGS_GENERAL: ConfigDoc[] = [
  {
    id: 'settings.general.slack_channel_id',
    label: 'Slack Channel ID',
    summary: 'Optional Slack channel ID (e.g. C01234ABCDE) for direct @mention routing alongside the webhook.',
    howItWorks:
      'The classify-report and fast-filter edge functions read this channel ID and pass it to chat.postMessage (bot token path) so notifications land in your chosen channel. The Slack bot must be invited to the channel first (/invite @mushi in Slack). Pairs with the bot token (SLACK_BOT_TOKEN env secret) — set both for threaded replies when you dispatch a fix from Slack.',
    default: { value: 'unset' },
    backend: {
      table: 'project_settings',
      column: 'slack_channel_id',
      endpoint: 'PATCH /v1/admin/settings',
      readBy: ['classify-report edge function', 'fast-filter edge function', 'slack-interactions edge function'],
    },
    whenToChange: 'Set to your Slack channel ID (e.g. C0B82A322RW — found in channel Details) whenever you want new report notifications delivered there. Use "Send test" in General Settings to verify.',
  },
  {
    id: 'settings.general.slack_webhook_url',
    label: 'Slack Webhook URL',
    summary: 'Posts new high-severity reports and weekly digests to this Slack webhook.',
    howItWorks:
      'When a report is classified at high severity or above (or via the weekly summary cron), Mushi sends a formatted Slack message to this URL. Leave blank to disable Slack delivery without affecting in-app notifications.',
    default: { value: 'unset (Slack disabled)' },
    backend: {
      table: 'project_settings',
      column: 'slack_webhook_url',
      endpoint: 'PATCH /v1/admin/settings',
      readBy: ['classify-report edge function', 'judge-batch edge function', 'slack-interactions edge function'],
    },
    whenToChange:
      "Set this on day 1 if your team reviews bugs from Slack. Rotate it whenever the channel owner changes — webhooks don't expire, so a stale URL keeps posting until you replace it.",
  },
  {
    id: 'settings.general.sentry_dsn',
    label: 'Sentry DSN',
    summary: 'Project DSN used to forward Mushi reports back into Sentry as events.',
    howItWorks:
      'Optional outbound integration. When set, classified reports get sent to Sentry as `captureException`-style events with the report id and severity attached, so the Sentry → Mushi loop can be closed without leaving either tool.',
    default: { value: 'unset (no forwarding)' },
    backend: {
      table: 'project_settings',
      column: 'sentry_dsn',
      endpoint: 'PATCH /v1/admin/settings',
      readBy: ['tester-marketplace API route (forwardToSentryDsn)', 'published-apps API route'],
    },
    whenToChange:
      'Add this if you want Mushi reports visible in Sentry dashboards alongside crash data. Skip it if Sentry is purely the source — the Integrations page handles inbound webhooks separately.',
  },
  {
    id: 'settings.general.sentry_webhook_secret',
    label: 'Sentry Webhook Secret',
    summary: 'Shared HMAC secret that authenticates inbound Sentry user-feedback webhooks.',
    howItWorks:
      'The Sentry webhook handler verifies the `Sentry-Hook-Signature` HMAC against this secret before it accepts a payload. Mismatch → 401, the report is dropped. The same value must be set in Sentry → Settings → Webhooks.',
    default: { value: 'unset (inbound disabled)' },
    backend: {
      table: 'project_settings',
      column: 'sentry_webhook_secret',
      endpoint: 'PATCH /v1/admin/settings',
      readBy: ['POST /v1/webhooks/sentry (api route)'],
    },
    whenToChange:
      'Set this once when wiring inbound Sentry user feedback. Rotate it together with the Sentry-side value — never one without the other or every payload starts failing signature verification.',
  },
  {
    id: 'settings.general.sentry_consume_user_feedback',
    label: 'Consume Sentry User Feedback',
    summary:
      'When enabled, Sentry user-feedback submissions are mirrored into the Mushi report queue.',
    howItWorks:
      'Drives the inbound webhook handler. With the toggle on, every Sentry user-feedback event creates a fresh report (deduplicated by Sentry event id). With it off, payloads are acknowledged but ignored — useful when you want webhook signing wired without yet doubling your queue volume.',
    default: { value: 'true' },
    backend: {
      table: 'project_settings',
      column: 'sentry_consume_user_feedback',
      endpoint: 'PATCH /v1/admin/settings',
      readBy: ['POST /v1/webhooks/sentry (api route)'],
    },
    whenToChange:
      "Turn off temporarily when piloting Sentry on a noisy public app — re-enable once you're happy with the volume and your routing rules are in place.",
  },
  {
    id: 'settings.general.stage2_model',
    label: 'Classification model',
    summary: 'Which LLM writes the plain-English read on each report after noise is filtered out.',
    howItWorks:
      'Stage 2 is the deep classifier — it labels severity, category, intent, dedup hints, and reproduction steps. The choice trades cost vs depth: Sonnet 4.6 is the recommended default; Opus is slow but catches subtle cases; Haiku is cheap but rougher. The selected model is read on every report, so changes apply immediately to new traffic.',
    default: { value: 'claude-sonnet-4-6' },
    backend: {
      table: 'project_settings',
      column: 'stage2_model',
      endpoint: 'PATCH /v1/admin/settings',
      readBy: ['classify-report edge function'],
    },
    whenToChange:
      "Stay on Sonnet 4.6 unless cost is biting (drop to Haiku) or you're finding misses on subtle pattern reports (try Opus on a small slice via Prompt Lab first).",
    learnMore: {
      label: 'Architecture overview',
      href: 'https://kensaur.us/mushi-mushi/docs/concepts/architecture',
    },
  },
  {
    id: 'settings.general.stage1_confidence_threshold',
    label: 'Noise filter confidence',
    summary: 'How confident Mushi must be that a report is spam or test noise before dropping it.',
    howItWorks:
      'Every inbound report runs through Stage 1 (Haiku 4.5). If the model says "this is spam/test/noise" with confidence ≥ this threshold, the report is dropped before Stage 2 spends tokens on it. Higher = more strict (more reports survive to Stage 2, fewer false drops); lower = more aggressive culling (cheaper, slightly more false drops).',
    default: { value: '0.85', range: '0.50 – 0.99' },
    backend: {
      table: 'project_settings',
      column: 'stage1_confidence_threshold',
      endpoint: 'PATCH /v1/admin/settings',
      readBy: ['fast-filter edge function'],
    },
    whenToChange:
      'Raise to 0.90+ if you suspect Stage 1 is dropping real reports (check Anti-Gaming for "fast-filter rejected" with low confidence margins). Lower to ~0.70 if a public-facing form is flooding the queue with obvious noise.',
    learnMore: {
      label: 'Architecture overview',
      href: 'https://kensaur.us/mushi-mushi/docs/concepts/architecture',
    },
  },
  {
    id: 'settings.general.dedup_threshold',
    label: 'Dedup Similarity Threshold',
    summary:
      'Cosine similarity above which two reports are merged as duplicates instead of stored separately.',
    howItWorks:
      "After Stage 2, a pgvector lookup finds the nearest existing report by embedding distance. If similarity ≥ this value, the new report is attached to the existing cluster (its `dup_of` points at the canonical id and the cluster's occurrence count ticks up). Below it, the report stays separate.",
    default: { value: '0.82', range: '0.50 – 0.99' },
    backend: {
      table: 'project_settings',
      column: 'dedup_threshold',
      endpoint: 'PATCH /v1/admin/settings',
      readBy: ['classify-report edge function'],
    },
    whenToChange:
      "Raise to 0.88+ if you're seeing false merges (different bugs being lumped together). Lower to ~0.75 if duplicate clusters look thin and the same regression keeps appearing as separate reports.",
  },
  {
    id: 'settings.general.fix_branch_template',
    label: 'Fix Branch Template',
    summary:
      'Branch name pattern when Mushi opens a draft fix PR on GitHub.',
    howItWorks:
      'When auto-fix opens a PR, it derives the branch name from this template. Supported tokens are substituted per report: `{date}` → `YYYY-MM-DD` (UTC), `{category}` → the report category slug, `{shortId}` → the first 8 characters of the report UUID. Leave empty to fall back to the legacy scheme `mushi/fix-<shortId>-<timestamp36>`.',
    default: { value: 'mushi/fix/{date}-{category}-{shortId}' },
    backend: {
      table: 'project_settings',
      column: 'fix_branch_template',
      endpoint: 'PATCH /v1/admin/settings',
      readBy: ['fix-worker edge function'],
    },
    whenToChange:
      'Change it to match your team\u2019s branch convention (e.g. `fixes/{date}-{shortId}`) so Mushi PRs sort alongside your existing branches. Keep `{shortId}` in the template to guarantee uniqueness and avoid branch collisions across reports.',
  },
];

const SETTINGS_BYOK: ConfigDoc[] = [
  {
    id: 'settings.byok.anthropic_key',
    label: 'Anthropic (Claude) API Key',
    summary:
      'Your Anthropic key — used to read bugs in plain English and draft fixes. Stored encrypted; only a vault reference is saved in settings.',
    howItWorks:
      'When set, bug reading and fix drafting bill your Anthropic account instead of platform credits. The key is stored encrypted in Supabase Vault — rotation is instant. When unset, Mushi falls back to the platform default (if your plan includes one). Powers fast-filter, classify-report, and fix-worker.',
    default: { value: 'unset (uses platform default)' },
    backend: {
      table: 'project_settings',
      column: 'byok_anthropic_key_ref (vault://…)',
      endpoint: 'PUT /v1/admin/byok/anthropic',
      readBy: ['fast-filter', 'classify-report', 'fix-worker edge functions'],
    },
    whenToChange:
      "Set on day 1 if your plan is BYOK-only. Rotate when an engineer with key access leaves, or when Anthropic's usage console shows unfamiliar traffic.",
    learnMore: {
      label: 'Self-hosting & BYOK setup',
      href: 'https://kensaur.us/mushi-mushi/docs/self-hosting',
    },
  },
  {
    id: 'settings.byok.openai_key',
    label: 'OpenAI / OpenRouter API Key',
    summary:
      'Your OpenAI-compatible key — backup when Anthropic is down, and for judge scoring. Works with OpenRouter and other gateways via Base URL.',
    howItWorks:
      'Used as automatic failover when Anthropic returns 5xx, and as the judge fallback in the autofix loop. Pair with the Base URL preset chips below to route the same key through any OpenAI-compatible gateway without code changes.',
    default: { value: 'unset (failover disabled)' },
    backend: {
      table: 'project_settings',
      column: 'byok_openai_key_ref (vault://…)',
      endpoint: 'PUT /v1/admin/byok/openai',
      readBy: ['fast-filter', 'classify-report', 'judge-batch edge function'],
    },
    whenToChange:
      'Add this once Anthropic outages start showing up in your error budget — the failover silently kicks in only when this is configured.',
  },
  {
    id: 'settings.byok.openai_base_url',
    label: 'OpenAI Base URL',
    summary:
      'Override the OpenAI endpoint to route the same key through OpenRouter, Together, Fireworks, or any compatible gateway.',
    howItWorks:
      "The OpenAI client honours this URL for every request. Leave blank to hit `api.openai.com`. The preset chips below populate common gateways so you don't have to remember the exact path.",
    default: { value: 'empty (api.openai.com)' },
    backend: {
      table: 'project_settings',
      column: 'byok_openai_base_url',
      endpoint: 'PUT /v1/admin/byok/openai',
      readBy: ['classify-report edge function'],
    },
    whenToChange:
      'Switch to OpenRouter when you want to A/B different models (Llama, Mixtral, Gemini) under one key. Switch back to blank when troubleshooting — eliminates the gateway as a variable.',
  },
];

const SETTINGS_FIRECRAWL: ConfigDoc[] = [
  {
    id: 'settings.firecrawl.api_key',
    label: 'Firecrawl API Key',
    summary:
      'Your Firecrawl key — lets Mushi look up docs and release notes while reviewing a bug or drafting a fix.',
    howItWorks:
      'When set, Research can crawl URLs during review; auto-fix pulls web snippets when local context is thin; a weekly cron checks dependency release notes. Stored encrypted in Vault. Leave unset to skip web research entirely.',
    default: { value: 'unset (web research disabled)' },
    backend: {
      table: 'project_settings',
      column: 'firecrawl_api_key_ref (vault://…)',
      endpoint: 'PUT /v1/admin/byok/firecrawl',
      readBy: ['settings-research API routes', 'fix-worker edge function', 'library-modernizer edge function'],
    },
    whenToChange:
      'Add this once you start seeing autofix attempts hit a wall on "library X changed its API". Skip it for offline-first projects or fully air-gapped deployments.',
  },
  {
    id: 'settings.firecrawl.allowed_domains',
    label: 'Firecrawl Allowed Domains',
    summary: 'Domain allowlist that bounds which hosts Firecrawl can scrape on your behalf.',
    howItWorks:
      "One host per line. The shared crawler helper rejects any URL whose host doesn't match an entry — exact match, no wildcards, no subdomain implied. An empty list means unrestricted (the crawler accepts any reachable host).",
    default: { value: 'empty (unrestricted)' },
    backend: {
      table: 'project_settings',
      column: 'firecrawl_allowed_domains',
      endpoint: 'PUT /v1/admin/byok/firecrawl',
      readBy: ['_shared/firecrawl helper (settings-research, fix-worker, library-modernizer)'],
    },
    whenToChange:
      "Lock this down to your stack's docs (`react.dev`, `nextjs.org`, `developer.mozilla.org`, etc.) when compliance demands provenance for any external content the LLM sees. Leave empty for open exploration during early adoption.",
  },
  {
    id: 'settings.firecrawl.max_pages_per_call',
    label: 'Firecrawl Max Pages per Call',
    summary:
      'Hard cap on pages a single Firecrawl crawl can fetch — prevents one bad request from draining your quota.',
    howItWorks:
      'The crawler helper caps each `crawlAndScrape` invocation at this number, regardless of what the calling code asks for. Caps stack: this is the per-call ceiling, on top of any per-day quota set by Firecrawl.',
    default: { value: '5', range: '1 – 20' },
    backend: {
      table: 'project_settings',
      column: 'firecrawl_max_pages_per_call',
      endpoint: 'PUT /v1/admin/byok/firecrawl',
      readBy: ['_shared/firecrawl helper'],
    },
    whenToChange:
      "Raise to 10–15 when fix-augmentation is consistently hitting the cap and the judge isn't getting enough context. Lower to 2–3 once your Firecrawl bill becomes the noisy line item.",
  },
];

const SETTINGS_DEVTOOLS: ConfigDoc[] = [
  {
    id: 'settings.devtools.debug_mode',
    label: 'Debug Mode',
    summary:
      'Local-only toggle that prints every API call, auth event, and timing to the browser console.',
    howItWorks:
      'Persists to `localStorage` under `mushi:debug` and reloads the page so the logger picks up the new mode at boot. Touches no backend state — purely a developer aid for diagnosing admin-side issues.',
    default: { value: 'off' },
    whenToChange:
      'Flip on when chasing an admin UI bug, then flip back off — the console output is verbose enough that it slows page interactions noticeably.',
  },
];

const PROJECTS: ConfigDoc[] = [
  {
    id: 'projects.create_project',
    label: 'Project name',
    summary: 'Names a new project — the bucket every report, key, and integration is scoped to.',
    howItWorks:
      'Project names appear in the switcher, on every report, and in webhook payloads — keep them recognisable to humans. Slugs are auto-derived from the name (lowercased, hyphenated) and used in URLs / API keys, so very short or generic names produce ambiguous slugs across orgs. After you click Create, the success panel shows the Project UUID to paste into the CLI (`MUSHI_PROJECT_ID`).',
    default: { value: 'unset (you must pick a name)' },
    backend: {
      table: 'projects',
      column: 'name',
      endpoint: 'POST /v1/admin/projects',
      readBy: ['ProjectSwitcher', 'every admin endpoint that scopes by project'],
    },
    whenToChange:
      "Set when adding a new app, environment, or customer. Rename later via the API if your team rebrands — slugs persist, names don't affect routing.",
  },
  {
    id: 'projects.project_id_copy',
    label: 'Project ID (UUID)',
    summary: 'The UUID your SDK and CLI use as MUSHI_PROJECT_ID — copy from the chip or the post-create success panel.',
    howItWorks:
      'Every report send and MCP call scopes to this id. The CLI wizard accepts UUID or proj_* slug form; the console always displays the UUID. Click the chip on Projects to copy — paste into `mushi init`, `mushi connect`, or `.env.local`.',
    default: { value: 'auto-generated on create' },
    whenToChange: 'Rarely — create a new project instead of reusing IDs across unrelated apps.',
  },
  {
    id: 'onboarding.cli_setup',
    label: 'CLI setup mode',
    summary: 'Deep link opened by `npx mushi-mushi` when you need to create a project first.',
    howItWorks:
      'URL flag `?setup=cli` on Setup → Steps shows CLI-oriented copy and keeps the success panel visible until you copy the Project ID and CLI commands. Pair with `mushi login` or paste credentials back into the wizard.',
    default: { value: '/onboarding?tab=steps&setup=cli' },
    whenToChange: 'Follow this path whenever the terminal asks for a Project ID you do not have yet.',
  },
  {
    id: 'projects.api_key_scope',
    label: 'API key scope preset',
    summary:
      'Chooses what a new API key can do — send bugs from your app, read reports in your editor, or both read and write.',
    howItWorks:
      "Each preset maps to scopes stored on the key — edge functions check scope before serving the call, so a leaked SDK key can't mutate state. SDK send-only = report submission. MCP read = browse reports in Cursor. MCP read + write = dispatch fixes from the editor.",
    default: { value: 'SDK send-only (report:write only)' },
    backend: {
      table: 'project_api_keys',
      column: 'scopes (jsonb)',
      endpoint: 'POST /v1/admin/projects/{id}/keys',
      readBy: ['POST /v1/reports (api route)', 'mcp server (@mushi-mushi/mcp)'],
    },
    whenToChange:
      'Pick `SDK send-only` for keys you ship inside a browser bundle. Pick `MCP read-only` for safely letting an agent browse reports. Only pick `MCP read + write` for trusted local clients with an audit trail.',
  },
  {
    id: 'projects.key_scope.report_write',
    label: 'Key scope: report submission',
    summary: 'Lets the API key send new bug reports from your app.',
    howItWorks:
      'Required for the SDK to submit user-reported bugs. Without this scope, `POST /v1/reports` returns 403. Safe to ship in browser bundles — the Edge Function rate-limits per IP and validates payload shape before storing anything.',
    default: { value: 'enabled by default for new keys' },
    backend: {
      table: 'project_api_keys',
      column: 'scopes (jsonb)',
      endpoint: 'POST /v1/admin/projects/{id}/keys',
      readBy: ['POST /v1/reports (api route)'],
    },
    whenToChange:
      'Always grant this for keys used by the SDK. Drop it for back-office keys that only need to read state (admin dashboards, MCP read-only).',
  },
  {
    id: 'projects.key_scope.mcp_read',
    label: 'Key scope: mcp:read',
    summary:
      'Lets the MCP server expose read-only resources (reports, projects, prompts) to LLM clients.',
    howItWorks:
      'The MCP server bundled at `packages/mcp` reads this scope to decide which tools / resources to register. Without it, only the public health-probe tools work.',
    default: { value: 'disabled by default — opt-in via the mint dialog' },
    backend: {
      table: 'project_api_keys',
      column: 'scopes (jsonb)',
      endpoint: 'POST /v1/admin/projects/{id}/keys',
      readBy: ['mcp server (@mushi-mushi/mcp)'],
    },
    whenToChange:
      'Grant on keys you paste into Claude Desktop / Cursor / Cline so the LLM can read your reports inbox. Pair with `mcp:write` only if you want the LLM to mutate state.',
  },
  {
    id: 'projects.key_scope.mcp_write',
    label: 'Key scope: mcp:write',
    summary:
      'Allows MCP tool calls to mutate state — transition report status, dispatch fixes, merge PRs, post replies.',
    howItWorks:
      'Mutating MCP tools (`dispatch_fix`, `transition_status`, `merge_fix`, `reopen_report`, etc.) gate on this scope. Add it sparingly — anything an LLM can call from a paste-able key, an attacker with the same key can call too.',
    default: { value: 'disabled by default' },
    backend: {
      table: 'project_api_keys',
      column: 'scopes (jsonb)',
      endpoint: 'POST /v1/admin/projects/{id}/keys',
      readBy: ['mcp server (@mushi-mushi/mcp)'],
    },
    whenToChange:
      "Only when you've scoped the key to a single trusted client (e.g. one developer's Cursor) and have an audit trail in place. Revoke + re-mint on personnel changes.",
  },
  {
    id: 'projects.active_project',
    label: 'Active Project',
    summary: "Picks which project's data the entire admin operates on for this session.",
    howItWorks:
      'Every admin API call carries the active project id as a header. Switching here re-issues all queries — there\'s no manual "reload everything" step. Persisted per-user in your auth profile, so each operator can have their own default.',
    default: { value: 'first project the user has access to' },
    backend: {
      table: 'auth.users / user_profile',
      column: 'active_project_id',
      endpoint: 'PATCH /v1/admin/me/active-project',
      readBy: ['every admin API endpoint'],
    },
    whenToChange:
      'Switch when reviewing bugs across multiple apps. For SSO orgs, ask the workspace owner to scope your invite so the picker only shows projects you should see.',
  },
];

const INTEGRATIONS: ConfigDoc[] = [
  {
    id: 'integrations.sentry.org_slug',
    label: 'Sentry org slug',
    summary:
      'Identifies your Sentry organization in API calls — the bit after `sentry.io/organizations/`.',
    howItWorks:
      'Used to scope every Sentry API call (issues, events, Seer root-cause). Wrong slug → 404 on every request and the integration goes red.',
    default: { value: 'unset' },
    backend: {
      table: 'platform_integrations',
      column: 'config.sentry_org_slug',
      endpoint: 'PUT /v1/admin/integrations/sentry',
      readBy: ['sentry-seer-poll edge function', 'integration-health-probe edge function'],
    },
    whenToChange: 'Set once at install. Update only if Sentry renames your org (rare).',
  },
  {
    id: 'integrations.sentry.project_slug',
    label: 'Sentry project slug',
    summary:
      'The specific Sentry project Mushi correlates against for stack traces and breadcrumbs.',
    howItWorks:
      'Scopes the event search when enriching a report. Leave blank to search across all projects under the org (slower; recommended only for tiny orgs).',
    default: { value: 'unset (org-wide search)' },
    backend: {
      table: 'platform_integrations',
      column: 'config.sentry_project_slug',
      endpoint: 'PUT /v1/admin/integrations/sentry',
      readBy: ['sentry-seer-poll edge function', 'integration-health-probe edge function'],
    },
    whenToChange:
      'Set this whenever you have more than one Sentry project — the speed-up on enrichment is significant.',
  },
  {
    id: 'integrations.sentry.auth_token',
    label: 'Sentry auth token',
    summary:
      'User-level Sentry token granting `project:read` + `event:read` for enrichment lookups.',
    howItWorks:
      'Stored as a vault reference (`vault://id`) — never in plaintext. The enricher uses it to fetch the matching event payload for a report.',
    default: { value: 'unset (enrichment disabled)' },
    backend: {
      table: 'platform_integrations',
      column: 'config.sentry_auth_token_ref',
      endpoint: 'PUT /v1/admin/integrations/sentry',
      readBy: ['sentry-seer-poll edge function', 'integration-health-probe edge function'],
    },
    whenToChange: 'Rotate quarterly, or whenever the issuing user leaves the org.',
  },
  {
    id: 'integrations.langfuse.host',
    label: 'Langfuse host',
    summary: 'Base URL of your Langfuse instance — cloud or self-hosted.',
    howItWorks:
      'Every LLM call (Stage 1, Stage 2, fix-worker, judge) emits a trace to this host. Stripped of trailing slashes server-side, so paste either form.',
    default: { value: 'unset (tracing disabled)' },
    backend: {
      table: 'platform_integrations',
      column: 'config.langfuse_host',
      endpoint: 'PUT /v1/admin/integrations/langfuse',
      readBy: ['fast-filter', 'classify-report', 'fix-worker', 'judge-batch'],
    },
    whenToChange:
      'Set on day 1 — tracing is the only way to see what the LLM actually saw when it misclassifies.',
  },
  {
    id: 'integrations.langfuse.public_key',
    label: 'Langfuse public key',
    summary: 'Pairs with the secret key for HTTP Basic auth against the Langfuse ingest endpoint.',
    howItWorks:
      'Sent as the username portion of every trace POST. Safe to commit — the secret key is what gates writes.',
    default: { value: 'unset' },
    backend: {
      table: 'platform_integrations',
      column: 'config.langfuse_public_key_ref',
      endpoint: 'PUT /v1/admin/integrations/langfuse',
      readBy: ['LLM observability layer'],
    },
    whenToChange: 'Rotate together with the secret key whenever you suspect either is leaked.',
  },
  {
    id: 'integrations.langfuse.secret_key',
    label: 'Langfuse secret key',
    summary: 'Pairs with the public key — gates trace writes from Mushi to Langfuse.',
    howItWorks:
      'Stored as a vault reference. Sent as the password portion of HTTP Basic auth on every trace POST.',
    default: { value: 'unset' },
    backend: {
      table: 'platform_integrations',
      column: 'config.langfuse_secret_key_ref',
      endpoint: 'PUT /v1/admin/integrations/langfuse',
      readBy: ['LLM observability layer'],
    },
    whenToChange: 'Rotate quarterly, or immediately on any suspicion of leak.',
  },
  {
    id: 'integrations.github.repo_url',
    label: 'GitHub repo URL',
    summary: 'The code repository the auto-fix worker opens draft PRs against.',
    howItWorks:
      'Full HTTPS URL (SSH URLs are normalised). The fix-worker clones the default branch, applies the LLM patch on a feature branch, and pushes a draft PR with the report id in the body.',
    default: { value: 'unset (autofix disabled)' },
    backend: {
      table: 'platform_integrations',
      column: 'config.github_repo_url',
      endpoint: 'PUT /v1/admin/integrations/github',
      readBy: ['fix-worker edge function'],
    },
    whenToChange:
      'Set when you graduate from "review only" to "Mushi opens PRs". Typically the production repo, not a sandbox.',
  },
  {
    id: 'integrations.github.default_branch',
    label: 'GitHub default branch',
    summary: 'Branch the fix-worker checks out before applying the LLM-generated patch.',
    howItWorks:
      'Defaults to `main` when blank. Override for repos that branch from `master`, `develop`, or a release line. The branch name is also used as the PR base.',
    default: { value: 'main' },
    backend: {
      table: 'platform_integrations',
      column: 'config.github_default_branch',
      endpoint: 'PUT /v1/admin/integrations/github',
      readBy: ['fix-worker edge function'],
    },
    whenToChange:
      "Change only if your repo's default isn't `main` — otherwise leaving it blank is the right answer.",
  },
  {
    id: 'integrations.github.installation_token',
    label: 'GitHub installation token',
    summary:
      'GitHub App installation token (preferred) or fine-grained PAT used to push branches and open PRs.',
    howItWorks:
      'Needs `Contents:write` + `Pull requests:write` on the target repo. Stored as a vault reference. App tokens are preferred — they auto-rotate and have a shorter blast radius than PATs.',
    default: { value: 'unset' },
    backend: {
      table: 'platform_integrations',
      column: 'config.github_installation_token_ref',
      endpoint: 'PUT /v1/admin/integrations/github',
      readBy: ['fix-worker edge function'],
    },
    whenToChange:
      'Re-issue when the GitHub App is uninstalled/reinstalled, or when a PAT hits its expiry.',
  },
  {
    id: 'integrations.github.webhook_secret',
    label: 'GitHub webhook secret',
    summary: 'HMAC secret that authenticates inbound check-run / check-suite webhooks from GitHub.',
    howItWorks:
      "Mushi's webhook handler verifies the `X-Hub-Signature-256` header against this secret. Without a match → 401, the event is dropped. The same value must be set in the GitHub repo Settings → Webhooks.",
    default: { value: 'unset (CI sync disabled)' },
    backend: {
      table: 'platform_integrations',
      column: 'config.github_webhook_secret',
      endpoint: 'PUT /v1/admin/integrations/github',
      readBy: ['github-webhook edge function'],
    },
    whenToChange:
      'Set this once you want PR check-run conclusions (CI passing/failing) reflected in the Auto-Fix Pipeline UI.',
  },
  {
    id: 'integrations.routing.jira.base_url',
    label: 'Jira base URL',
    summary: 'Your Atlassian Cloud or Server base URL — issues and links resolve relative to this.',
    howItWorks:
      'Used to build issue URLs (`{baseUrl}/browse/{key}`) and as the API host for create/update calls. Cloud URLs typically end in `.atlassian.net`.',
    default: { value: 'unset' },
    backend: {
      table: 'routing_destinations',
      column: 'config.baseUrl',
      endpoint: 'POST /v1/admin/routing',
      readBy: ['route-to-jira edge function'],
    },
    whenToChange:
      'Update if Atlassian migrates your tenant or you self-host Jira behind a new domain.',
  },
  {
    id: 'integrations.routing.jira.email',
    label: 'Jira user email',
    summary:
      'Atlassian account email that owns the API token below — Jira pairs them as basic auth.',
    howItWorks:
      'Sent as the username for every Jira request. Pair with the API token in the next field.',
    default: { value: 'unset' },
    backend: {
      table: 'routing_destinations',
      column: 'config.email',
      endpoint: 'POST /v1/admin/routing',
      readBy: ['route-to-jira edge function'],
    },
    whenToChange:
      'Set this to a service account, not a real human — service accounts survive offboarding.',
  },
  {
    id: 'integrations.routing.jira.api_token',
    label: 'Jira API token',
    summary: 'Atlassian API token paired with the email above for basic auth.',
    howItWorks: 'Stored as vault reference. Create at id.atlassian.com → Security → API tokens.',
    default: { value: 'unset' },
    backend: {
      table: 'routing_destinations',
      column: 'config.apiToken',
      endpoint: 'POST /v1/admin/routing',
      readBy: ['route-to-jira edge function'],
    },
    whenToChange: 'Rotate quarterly. Re-issue immediately if the owning email changes.',
  },
  {
    id: 'integrations.routing.jira.project_key',
    label: 'Jira project key',
    summary:
      'Short uppercase code that prefixes every issue in the target project (e.g. `BUG`, `MUSHI`).',
    howItWorks:
      'Used in the `POST /rest/api/3/issue` payload as `fields.project.key`. The created issues get keys like `BUG-123`. Wrong key → Jira rejects the create call.',
    default: { value: 'unset' },
    backend: {
      table: 'routing_destinations',
      column: 'config.projectKey',
      endpoint: 'POST /v1/admin/routing',
      readBy: ['route-to-jira edge function'],
    },
    whenToChange:
      'Change to route to a different Jira project — typically when the support team owns a new tracker.',
  },
  {
    id: 'integrations.routing.linear.api_key',
    label: 'Linear API key',
    summary: 'Personal API key used to mirror reports as Linear issues.',
    howItWorks:
      'Sent as the `Authorization` header on every Linear GraphQL call. Stored as vault reference. Generate at Linear → Settings → API → Personal API keys.',
    default: { value: 'unset' },
    backend: {
      table: 'routing_destinations',
      column: 'config.apiKey',
      endpoint: 'POST /v1/admin/routing',
      readBy: ['route-to-linear edge function'],
    },
    whenToChange:
      "Rotate when the issuing user changes role. Linear keys don't auto-expire, so quarterly review is wise.",
  },
  {
    id: 'integrations.routing.linear.team_id',
    label: 'Linear team ID',
    summary: 'UUID of the Linear team that should receive mirrored issues.',
    howItWorks:
      'Used as the `teamId` argument on `issueCreate`. Find it in Linear → Settings → API → "Find your team ID".',
    default: { value: 'unset' },
    backend: {
      table: 'routing_destinations',
      column: 'config.teamId',
      endpoint: 'POST /v1/admin/routing',
      readBy: ['route-to-linear edge function'],
    },
    whenToChange:
      'Update when re-routing to a different team — e.g. moving from Triage to Engineering once the team grows.',
  },
  {
    id: 'integrations.routing.github_issues.token',
    label: 'GitHub Issues PAT',
    summary: 'Fine-grained PAT with `Issues:write` on the public-tracker repo.',
    howItWorks:
      'Distinct from the auto-fix repo PAT — this one targets the tracker repo (often public), not the code repo. Stored as vault reference.',
    default: { value: 'unset' },
    backend: {
      table: 'routing_destinations',
      column: 'config.token',
      endpoint: 'POST /v1/admin/routing',
      readBy: ['route-to-github-issues edge function'],
    },
    whenToChange:
      'Use when you want a public-facing changelog of reviewed bugs without exposing your code repo.',
  },
  {
    id: 'integrations.routing.github_issues.owner',
    label: 'GitHub Issues owner',
    summary:
      'Org or user that owns the issue-tracker repo (the bit before the slash in `owner/repo`).',
    howItWorks: 'Concatenated into the GitHub API path: `/repos/{owner}/{repo}/issues`.',
    default: { value: 'unset' },
    backend: {
      table: 'routing_destinations',
      column: 'config.owner',
      endpoint: 'POST /v1/admin/routing',
      readBy: ['route-to-github-issues edge function'],
    },
    whenToChange:
      'Change when the tracker repo moves under a new org — typically during company rebranding.',
  },
  {
    id: 'integrations.routing.github_issues.repo',
    label: 'GitHub Issues repo',
    summary: 'Repository name (no owner prefix) that issues are filed under.',
    howItWorks:
      'Concatenated with the owner above into the API path. Case-sensitive on the GitHub API side.',
    default: { value: 'unset' },
    backend: {
      table: 'routing_destinations',
      column: 'config.repo',
      endpoint: 'POST /v1/admin/routing',
      readBy: ['route-to-github-issues edge function'],
    },
    whenToChange:
      'Update when archiving and replacing the tracker — Mushi follows the new repo as soon as you save.',
  },
  {
    id: 'integrations.routing.pagerduty.routing_key',
    label: 'PagerDuty routing key',
    summary:
      '32-character integration key for PagerDuty Events API v2 — pages on-call when severity = critical.',
    howItWorks:
      'Mushi POSTs a v2 event payload with `event_action=trigger` and a fingerprint built from the report cluster id, so duplicate criticals dedupe instead of paging twice. Auto-resolves the incident when the linked report closes.',
    default: { value: 'unset' },
    backend: {
      table: 'routing_destinations',
      column: 'config.routingKey',
      endpoint: 'POST /v1/admin/routing',
      readBy: ['route-to-pagerduty edge function'],
    },
    whenToChange:
      "Set this once you have a real on-call rotation. Don't use a personal key — use a service-level integration key.",
  },
];

const STORAGE: ConfigDoc[] = [
  {
    id: 'storage.provider',
    label: 'Storage provider',
    summary:
      'Which object-storage backend hosts user-uploaded artifacts (screenshots, recordings, attachments).',
    howItWorks:
      'Switches the storage adapter used for new uploads — supabase (default, lives in Supabase Storage), s3 (any S3-compatible host), gcs (Google Cloud Storage), or r2 (Cloudflare R2). Existing artifacts stay where they were written; only new traffic moves.',
    default: { value: 'supabase' },
    backend: {
      table: 'storage_configs',
      column: 'provider',
      endpoint: 'PUT /v1/admin/storage',
      readBy: ['storage adapter (every artifact upload/download)'],
    },
    whenToChange:
      'Switch to your own bucket once you cross the Supabase Storage egress free tier, or when compliance asks you to keep artifacts inside your own VPC.',
  },
  {
    id: 'storage.bucket',
    label: 'Bucket',
    summary: 'Name of the bucket the storage adapter writes artifacts into.',
    howItWorks:
      'Must already exist on the chosen provider — Mushi never creates buckets implicitly (avoids accidental data scattering across regions). The IAM identity behind the credentials below needs `s3:PutObject` / `s3:GetObject` (or the equivalent) on this bucket.',
    default: { value: 'unset' },
    backend: {
      table: 'storage_configs',
      column: 'bucket',
      endpoint: 'PUT /v1/admin/storage',
      readBy: ['storage adapter'],
    },
    whenToChange:
      'Set once when wiring BYO storage. Migrate to a new bucket only with a backfill plan — old links keep pointing at the old one.',
  },
  {
    id: 'storage.region',
    label: 'Region',
    summary:
      'Geographic region of the bucket — used to build the endpoint and for residency enforcement.',
    howItWorks:
      'For S3, the region is part of the URL signing process; mismatch → SignatureDoesNotMatch. For Compliance: storage region is checked against `data_residency_region` and a mismatch triggers a hard refusal at write time.',
    default: { value: 'unset' },
    backend: {
      table: 'storage_configs',
      column: 'region',
      endpoint: 'PUT /v1/admin/storage',
      readBy: ['storage adapter', 'compliance check'],
    },
    whenToChange:
      "Set the region your bucket actually lives in. Don't guess — write failures from a wrong region are silent until the user can't open their screenshot.",
  },
  {
    id: 'storage.endpoint',
    label: 'Endpoint',
    summary:
      'Full URL of the storage API endpoint — only needed for non-AWS S3-compatible providers.',
    howItWorks:
      'Leave blank for AWS S3 (the SDK builds the URL from region). Set explicitly for R2 (`https://<account>.r2.cloudflarestorage.com`), Backblaze B2, MinIO, or any other S3-compatible host.',
    default: { value: 'empty (provider default)' },
    backend: {
      table: 'storage_configs',
      column: 'endpoint',
      endpoint: 'PUT /v1/admin/storage',
      readBy: ['storage adapter'],
    },
    whenToChange:
      'Set once when pointing at a non-AWS S3 host. Update if your provider migrates accounts to a new endpoint shape.',
  },
  {
    id: 'storage.path_prefix',
    label: 'Path prefix',
    summary:
      'String prepended to every key the storage adapter writes — segments artifacts inside a shared bucket.',
    howItWorks:
      'A trailing slash is added if missing. Useful when one bucket hosts multiple Mushi projects or coexists with other apps — set to `mushi/prod/` so your tooling can audit just the Mushi paths without confusing them with neighbours.',
    default: { value: 'empty (writes to bucket root)' },
    backend: {
      table: 'storage_configs',
      column: 'path_prefix',
      endpoint: 'PUT /v1/admin/storage',
      readBy: ['storage adapter'],
    },
    whenToChange:
      "Set when sharing a bucket. Don't change after writes have started — old keys stay where they were.",
  },
  {
    id: 'storage.signed_url_ttl_secs',
    label: 'Signed URL TTL (seconds)',
    summary:
      'Lifetime of presigned download URLs handed out to admin users for screenshots and recordings.',
    howItWorks:
      'Every download in the admin (e.g. preview a screenshot, replay a session recording) is gated by a fresh presigned URL. Lower = tighter security (links expire fast); higher = friendlier UX (a copied link still works in a Slack thread an hour later).',
    default: { value: '3600 (1 hour)', range: '60 – 604800 (7 days)' },
    backend: {
      table: 'storage_configs',
      column: 'signed_url_ttl_secs',
      endpoint: 'PUT /v1/admin/storage',
      readBy: ['storage adapter (every signed URL it mints)'],
    },
    whenToChange:
      'Drop to 5–15 minutes for high-sensitivity data. Bump up to a day if your team works asynchronously and copies links into long-running threads.',
  },
  {
    id: 'storage.access_key_ref',
    label: 'Access key (vault ref)',
    summary:
      'Reference to the access-key half of the storage credentials — stored in Supabase Vault, never plaintext.',
    howItWorks:
      'Form takes the raw key on save and stashes it in Vault, then stores only `vault://<id>` here. The adapter resolves the secret at request time.',
    default: { value: 'unset' },
    backend: {
      table: 'storage_configs',
      column: 'access_key_ref',
      endpoint: 'PUT /v1/admin/storage',
      readBy: ['storage adapter'],
    },
    whenToChange: 'Rotate quarterly, or immediately if the key may have leaked.',
  },
  {
    id: 'storage.secret_key_ref',
    label: 'Secret key (vault ref)',
    summary: 'Reference to the secret-key half of the storage credentials.',
    howItWorks:
      'Same Vault flow as the access key. Pair must be rotated together — half-rotations leave the adapter unable to sign.',
    default: { value: 'unset' },
    backend: {
      table: 'storage_configs',
      column: 'secret_key_ref',
      endpoint: 'PUT /v1/admin/storage',
      readBy: ['storage adapter'],
    },
    whenToChange:
      'Rotate alongside the access key. Never paste the raw value into an email or ticket.',
  },
  {
    id: 'storage.kms_key_id',
    label: 'KMS key ID',
    summary: 'Optional customer-managed KMS key used to encrypt artifacts at rest with SSE-KMS.',
    howItWorks:
      "When set, every PutObject specifies `x-amz-server-side-encryption: aws:kms` with this key id. Without it, the bucket's default encryption applies (typically AES256).",
    default: { value: 'empty (provider default encryption)' },
    backend: {
      table: 'storage_configs',
      column: 'kms_key_id',
      endpoint: 'PUT /v1/admin/storage',
      readBy: ['storage adapter'],
    },
    whenToChange:
      'Set when compliance demands customer-managed encryption keys. Verify the IAM principal has `kms:Encrypt`/`kms:Decrypt` on the key ARN.',
  },
];

const COMPLIANCE: ConfigDoc[] = [
  {
    id: 'compliance.residency.region',
    label: 'Data residency region',
    summary:
      "Pins your project's data to a specific geographic region — `us`, `eu`, `jp`, or `self` (BYO storage).",
    howItWorks:
      'On first set, Mushi pins the project to this region and validates that storage + DB + edge functions all run there. Once pinned, the value is REGION_LOCKED — subsequent change attempts return 409 with `code: REGION_LOCKED`. To migrate, open a support ticket so the data move can be audited.',
    default: { value: 'unpinned (first traffic locks it)' },
    backend: {
      table: 'projects',
      column: 'data_residency_region',
      endpoint: 'PUT /v1/admin/residency/{projectId}',
      readBy: ['every storage write', 'compliance check'],
    },
    whenToChange:
      "Set on day 1 if compliance demands it (HIPAA, GDPR, J-SOX). Don't set speculatively — the lock-out is real and reversal is manual.",
    learnMore: {
      label: 'Configuration reference',
      href: 'https://github.com/kensaurus/mushi-mushi/blob/master/docs/CONFIG_REFERENCE.md',
    },
  },
  {
    id: 'compliance.retention.reports_days',
    label: 'Reports retention (days)',
    summary:
      'How long classified reports stay in the database before the retention sweeper deletes them.',
    howItWorks:
      'A nightly cron deletes any report whose `created_at + reports_retention_days` is in the past, UNLESS `legal_hold` is true (in which case nothing is deleted regardless of age). Soft-delete first (90-day tombstone), then hard-delete.',
    default: { value: '365 (1 year)' },
    backend: {
      table: 'project_retention_policies',
      column: 'reports_retention_days',
      endpoint: 'PUT /v1/admin/compliance/retention/{projectId}',
      readBy: ['retention-sweep cron', 'soc2-evidence edge function'],
    },
    whenToChange:
      'Lower to 90 for GDPR-tight projects. Raise to 730+ for regulated industries that need multi-year audit history.',
  },
  {
    id: 'compliance.retention.audit_days',
    label: 'Audit log retention (days)',
    summary: 'How long admin-action audit logs (who saw / changed / deleted what) are retained.',
    howItWorks:
      'Independent of the reports retention. Audit logs are append-only and rarely need to be the same age as the underlying data — most regulators want 1–7 years of audit even on 90-day data.',
    default: { value: '730 (2 years)' },
    backend: {
      table: 'project_retention_policies',
      column: 'audit_retention_days',
      endpoint: 'PUT /v1/admin/compliance/retention/{projectId}',
      readBy: ['audit-sweep cron', 'soc2-evidence edge function'],
    },
    whenToChange:
      'Match your strictest regulatory ask (SOC 2 typically asks for 1y; HIPAA 6y; J-SOX 7y).',
  },
  {
    id: 'compliance.retention.attachments_days',
    label: 'Attachments retention (days)',
    summary: 'How long screenshots, recordings, and other binary attachments are retained.',
    howItWorks:
      "Object-storage entries are deleted via the configured storage adapter. If a report is older than its retention window but its attachments aren't, the attachments are orphaned but kept until their own clock expires.",
    default: { value: '180 (6 months)' },
    backend: {
      table: 'project_retention_policies',
      column: 'attachments_retention_days',
      endpoint: 'PUT /v1/admin/compliance/retention/{projectId}',
      readBy: ['attachment-sweep cron'],
    },
    whenToChange:
      'Lower aggressively if storage cost dominates. Raise only when artifacts are evidentiary (regulated reproduction steps).',
  },
  {
    id: 'compliance.retention.events_days',
    label: 'Events retention (days)',
    summary:
      'How long AI step events (LLM calls, report spans, fix attempts) are retained for analytics.',
    howItWorks:
      "Drives the rollup tables that power the Health page. Events older than this window are dropped; aggregated rollups (hourly/daily) survive longer because they're much smaller.",
    default: { value: '90' },
    backend: {
      table: 'project_retention_policies',
      column: 'events_retention_days',
      endpoint: 'PUT /v1/admin/compliance/retention/{projectId}',
      readBy: ['events-sweep cron', 'health-rollups cron'],
    },
    whenToChange:
      'Lower to 30 if the events table is your top storage line item. Raise to 365 if you do longitudinal pipeline analysis.',
  },
  {
    id: 'compliance.legal_hold',
    label: 'Legal hold',
    summary:
      'Master switch that suspends ALL retention deletes — for litigation holds and regulatory inquiries.',
    howItWorks:
      'When on, every retention sweeper short-circuits and deletes nothing. The toggle is itself audit-logged (who flipped it, when, why) so a compliance team can prove the hold was active during the incident window.',
    default: { value: 'off' },
    backend: {
      table: 'project_retention_policies',
      column: 'legal_hold',
      endpoint: 'PUT /v1/admin/compliance/retention/{projectId}',
      readBy: ['every retention sweeper'],
    },
    whenToChange:
      'Flip ON the moment counsel hands you a hold notice. Flip OFF only after counsel confirms the hold is released — leaving it on indefinitely defeats GDPR/CCPA right-to-be-forgotten.',
  },
  {
    id: 'compliance.dsar.subject_email',
    label: 'DSAR subject email',
    summary: 'Email address of the data subject whose data you want exported or deleted.',
    howItWorks:
      'The DSAR (Data Subject Access Request) endpoint searches every report, attachment, and event whose `reporter_email` or session metadata matches this address, and produces either an export bundle or a delete plan.',
    default: { value: 'unset' },
    backend: {
      table: 'dsar_requests',
      column: 'subject_email',
      endpoint: 'POST /v1/admin/compliance/dsar',
      readBy: ['dsar-runner edge function'],
    },
    whenToChange:
      "Fill in only when processing a real DSAR. Each submission creates an auditable request — don't test on real customer emails.",
  },
];

const SSO: ConfigDoc[] = [
  {
    id: 'sso.provider_type',
    label: 'SSO provider type',
    summary:
      'Which federation protocol the IdP speaks — `saml` (self-service) or `oidc` (audit-only — manual setup required).',
    howItWorks:
      "Determines which authn flow Mushi expects on the callback. SAML is fully self-service: Mushi calls the GoTrue Admin API to register the provider and returns the canonical ACS URL + Entity ID. OIDC is stored for audit but Mushi cannot auto-register it (GoTrue's Admin API does not yet expose an OIDC endpoint); selecting it returns HTTP 202 with `status: 'manual_required'` so you can quote the config id in a Supabase support ticket.",
    default: { value: 'saml' },
    backend: {
      table: 'sso_providers',
      column: 'provider_type',
      endpoint: 'PUT /v1/admin/sso',
      readBy: ['sso-callback edge function'],
    },
    whenToChange:
      "Pick what your IdP actually serves. Don't pick OIDC yet — the gate exists for safety, not capacity.",
  },
  {
    id: 'sso.metadata_url',
    label: 'IdP metadata URL',
    summary: "URL Mushi fetches to learn the IdP's certificates, endpoints, and assertion shape.",
    howItWorks:
      'Refetched daily so cert rotations propagate without a manual sync. If the URL goes 4xx, the previous cached metadata is used until it does.',
    default: { value: 'unset' },
    backend: {
      table: 'sso_providers',
      column: 'metadata_url',
      endpoint: 'PUT /v1/admin/sso',
      readBy: ['sso-callback edge function'],
    },
    whenToChange:
      'Update when migrating IdPs (Okta → Entra, etc.). Verify the new metadata URL is reachable from your Mushi region before flipping.',
  },
  {
    id: 'sso.entity_id',
    label: 'Entity ID',
    summary:
      'Unique identifier for this Mushi project as seen by the IdP — also called the audience.',
    howItWorks:
      'Sent in the SAML AuthnRequest as `Issuer` and asserted by the IdP in the response. Mismatch → assertion rejected.',
    default: { value: 'unset' },
    backend: {
      table: 'sso_providers',
      column: 'entity_id',
      endpoint: 'PUT /v1/admin/sso',
      readBy: ['sso-callback edge function'],
    },
    whenToChange:
      'Set once during provisioning. Match exactly what the IdP\'s app config has for "Audience URI".',
  },
  {
    id: 'sso.allowed_domains',
    label: 'SSO email domains',
    summary: 'Comma-separated email domains routed to this SSO provider (`acme.com, acme.co.jp`).',
    howItWorks:
      'On the login page, the email a user types is matched against this list — domain hit → redirect to SSO. Domain miss → fall back to password (or block, depending on the org\'s "SSO required" toggle).',
    default: { value: 'unset (no SSO routing)' },
    backend: {
      table: 'sso_providers',
      column: 'allowed_domains (text[])',
      endpoint: 'PUT /v1/admin/sso',
      readBy: ['login-resolver edge function'],
    },
    whenToChange:
      "Add a domain the day before that company's users start onboarding. Remove a domain immediately on contract end so old emails can't still SSO in.",
  },
];

const PROMPT_LAB: ConfigDoc[] = [
  {
    id: 'prompt-lab.stage',
    label: 'Pipeline stage',
    summary:
      "Picks which AI step you're editing prompts for — fast-filter, classifier, fix-worker, or judge.",
    howItWorks:
      "Each stage has its own active prompt id. Switching tabs scopes every action below (create new version, set traffic split, replay) to that stage's prompts only — they don't cross-pollinate.",
    default: { value: 'classifier (most-used)' },
    backend: {
      table: 'prompt_versions',
      column: 'stage',
      endpoint: 'GET /v1/admin/prompt-lab/prompts',
      readBy: ['_shared/prompt-ab helper'],
    },
    whenToChange:
      "Pick the stage you're iterating on. Most teams start with classifier — it has the largest impact per token.",
  },
  {
    id: 'prompt-lab.traffic_percentage',
    label: 'Traffic percentage',
    summary: 'Share of production traffic that gets this prompt version — 0% to 100%.',
    howItWorks:
      "A weighted random pick (`prompt-ab.ts`) routes each request to a prompt version based on its `traffic_percentage`. The percentages should sum to 100 across active versions of a stage; the helper normalises if they don't. Live changes apply within seconds — no deploy required.",
    default: { value: '0% on new versions' },
    backend: {
      table: 'prompt_versions',
      column: 'traffic_percentage',
      endpoint: 'POST /v1/admin/prompt-lab/prompts',
      readBy: ['_shared/prompt-ab helper'],
    },
    whenToChange:
      "Start a new version at 5%, watch the eval scores for 24h, then ramp 25→50→100. Don't flip 0→100 — you lose the ability to A/B against the previous champion.",
    learnMore: {
      label: 'Architecture overview',
      href: 'https://kensaur.us/mushi-mushi/docs/concepts/architecture',
    },
  },
  {
    id: 'prompt-lab.prompt_body',
    label: 'Prompt body',
    summary: 'The system + user templates the LLM sees, with named slots for runtime variables.',
    howItWorks:
      'Slots like `{report_text}` and `{recent_events}` get substituted at call time. The body is versioned — saving creates a new `prompt_versions` row, never overwrites an existing one. The previous version stays at its current traffic split until you move it.',
    default: { value: 'shipped baseline (varies by stage)' },
    backend: {
      table: 'prompt_versions',
      column: 'body',
      endpoint: 'POST /v1/admin/prompt-lab/prompts',
      readBy: ['fast-filter', 'classify-report', 'fix-worker, judge-batch'],
    },
    whenToChange:
      'When eval scores plateau or a new model rewards different prompting style. Tag every change with what you tried, so the changelog is honest.',
  },
  {
    id: 'prompt-lab.synthetic_count',
    label: 'Synthetic case count',
    summary:
      'How many synthetic test cases to generate when seeding evals for a new prompt version.',
    howItWorks:
      'Higher count = more statistical power on eval scores, but also more LLM spend (each case runs both the prompt and the judge). 25 is enough to spot regressions; 100+ is needed to detect <5% delta with confidence.',
    default: { value: '25', range: '5 – 200' },
    backend: {
      table: 'prompt_versions',
      column: '(driven by generator job, not stored)',
      endpoint: 'POST /v1/admin/prompt-lab/synthesize',
      readBy: ['eval-runner cron'],
    },
    whenToChange:
      'Bump to 100 when a regression is suspected and you need confidence; drop to 10 for quick smoke tests during iteration.',
  },
];

const MARKETPLACE: ConfigDoc[] = [
  {
    id: 'marketplace.plugin_webhook_url',
    label: 'Plugin webhook URL',
    summary: 'Where Mushi POSTs subscribed plugin events — your endpoint receives them.',
    howItWorks:
      'Every subscribed event fires a JSON POST against this URL. Failed posts retry with exponential backoff up to 24 hours; persistently-failing webhooks are quarantined and surfaced as an alert.',
    default: { value: 'unset (plugin disabled)' },
    backend: {
      table: 'marketplace_plugins',
      column: 'webhook_url',
      endpoint: 'POST /v1/admin/plugins',
      readBy: ['marketplace-dispatcher edge function'],
    },
    whenToChange:
      'Set when wiring a plugin. Update when your plugin host migrates — the dispatcher honours the new URL on the next event.',
  },
  {
    id: 'marketplace.plugin_signing_secret',
    label: 'Plugin signing secret',
    summary: 'HMAC secret your plugin verifies on every inbound event so it can trust the payload.',
    howItWorks:
      'Mushi signs every event with `X-Mushi-Signature: t=<ts>,v1=<hmac>`. Your plugin recomputes the HMAC against the raw body using this secret; mismatch = drop the event.',
    default: { value: 'auto-generated on plugin create' },
    backend: {
      table: 'marketplace_plugins',
      column: 'signing_secret_ref',
      endpoint: 'POST /v1/admin/plugins',
      readBy: ['marketplace-dispatcher edge function'],
    },
    whenToChange:
      'Rotate when the plugin owner changes hands. Always update both ends in lockstep — no overlap window.',
  },
  {
    id: 'marketplace.subscribed_events',
    label: 'Subscribed events',
    summary:
      'List of event types your plugin wants to receive (`report.created`, `report.classified`, `fix.opened`, etc.).',
    howItWorks:
      'The dispatcher emits to your URL only for events on this list — every other event is no-op for your plugin. Keep the list minimal; each event is a billable webhook delivery.',
    default: { value: 'empty (plugin receives nothing)' },
    backend: {
      table: 'marketplace_plugins',
      column: 'subscribed_events (text[])',
      endpoint: 'POST /v1/admin/plugins',
      readBy: ['marketplace-dispatcher edge function'],
    },
    whenToChange:
      'Subscribe only to events your plugin actually reacts to. Adding/removing is instant — no plugin restart required.',
  },
];

const ANTI_GAMING: ConfigDoc[] = [
  {
    id: 'anti-gaming.flagged_filter',
    label: 'Anti-gaming filter',
    summary:
      'Show only flagged reports, only clean ones, or both — scopes the table without losing your sort state.',
    howItWorks:
      "Drives the `?flagged=true|false` query param on the reports listing. Pure UI filter — doesn't change underlying data, doesn't mark anything reviewed.",
    default: { value: 'all' },
    whenToChange:
      'Switch to "flagged only" when investigating a suspected attack pattern. Switch back when you\'re reviewing the regular bug queue.',
  },
  {
    id: 'anti-gaming.aggregate_identical',
    label: 'Aggregate identical reports',
    summary:
      'Collapses runs of byte-identical reports into a single grouped row with an occurrence count.',
    howItWorks:
      'When on, the table group-bys on `content_hash` and shows one row per unique payload. Useful when one bot or one bug retries the same submission thousands of times — the grouping makes the actual diversity visible.',
    default: { value: 'on' },
    whenToChange:
      "Turn off when you need to see the timing distribution of a flood (the per-row timestamps tell you about cadence, the grouped view doesn't).",
  },
  {
    id: 'anti-gaming.flag_reason',
    label: 'Flag reason',
    summary:
      'Free-text note attached to a flag — explains *why* this report tripped the human reviewer.',
    howItWorks:
      'Persisted on the report and surfaced in the audit log. The next reviewer (or the LLM, if you wire it through Prompt Lab) can read this when deciding whether the flag still applies.',
    default: { value: 'empty' },
    backend: {
      table: 'reports',
      column: 'flag_reason',
      endpoint: 'PATCH /v1/admin/reports/{id}/flag',
      readBy: ['anti-gaming dashboard', 'audit log'],
    },
    whenToChange:
      'Always fill it in — "flagged with no reason" is the ticket the next person on rotation can\'t review.',
  },
];

const NOTIFICATIONS: ConfigDoc[] = [
  {
    id: 'notifications.show_filter',
    label: 'Show filter',
    summary:
      'Picks which notifications appear in the list — `all`, `unread only`, or per-category.',
    howItWorks:
      'Pure UI filter. Doesn\'t mark anything as read; doesn\'t mute future notifications. Persists in the URL so a deep link to "unread severities high+" survives a reload.',
    default: { value: 'unread' },
    whenToChange:
      'Stay on `unread` for daily review. Flip to `all` when looking for a specific notification you saw last week.',
  },
  {
    id: 'notifications.type_filter',
    label: 'Type filter',
    summary:
      'Filter the list to a single notification type (severity escalation, autofix opened, plugin error, etc.).',
    howItWorks:
      "Reads `?type=` from the URL. Multi-select isn't supported — pick one type at a time.",
    default: { value: 'all types' },
    whenToChange:
      'Use when chasing one class of notification (e.g. all "autofix opened") — keeps your inbox legible while you bulk-review.',
  },
];

const INTELLIGENCE: ConfigDoc[] = [
  {
    id: 'intelligence.benchmarking_optin',
    label: 'Benchmarking opt-in',
    summary:
      'When ON, your project contributes anonymised aggregate metrics to cross-customer benchmarks (which then power the Intelligence page comparisons).',
    howItWorks:
      "The intelligence helper inspects this flag before including a project in a percentile bucket. OFF = your data is never in the buckets, AND you don't see the bucketed share — the Intelligence page falls back to your own historical data.",
    default: { value: 'off' },
    backend: {
      table: 'project_settings',
      column: 'benchmarking_optin',
      endpoint: 'PUT /v1/admin/settings/benchmarking',
      readBy: ['_shared/intelligence helper'],
    },
    whenToChange:
      'Turn on if you want "you vs the median customer" comparisons. Keep off for projects under strict NDAs — even anonymised aggregates leak shape information.',
  },
];

const BILLING: ConfigDoc[] = [
  {
    id: 'billing.plan',
    label: 'Plan',
    summary: 'Subscription tier — determines the included LLM credits, MAU cap, and feature gates.',
    howItWorks:
      'Plan changes go through Stripe Checkout / Customer Portal — Mushi mirrors the new plan id back via webhook. Downgrades are queued to the end of the current period; upgrades take effect immediately and are pro-rated.',
    default: { value: 'free' },
    backend: {
      table: 'subscriptions',
      column: 'plan_id',
      endpoint: '(via Stripe webhook → /v1/billing/webhook)',
      readBy: ['feature-gate middleware', 'usage caps'],
    },
    whenToChange:
      "Upgrade when you're consistently hitting the cap on the dashboard. Downgrade only after one full month under the next-tier-down's cap.",
  },
  {
    id: 'billing.spend_cap',
    label: 'Monthly spend cap',
    summary:
      'Hard USD ceiling on overage charges. When projected spend hits the cap, new diagnoses pause gracefully — no surprise invoice.',
    howItWorks:
      'Stored on billing_subscriptions.monthly_spend_cap_usd_override and enforced in classify-report before Stage-2 runs. Overrides the plan default (Indie $50, Pro $200). Clear the field to revert to the plan default.',
    default: { value: 'plan default' },
    backend: {
      table: 'billing_subscriptions',
      column: 'monthly_spend_cap_usd_override',
      endpoint: 'PUT /v1/admin/billing/spend-cap',
      readBy: ['classify-report quota gate', 'usage-alerts email copy'],
    },
    whenToChange:
      'Set before a launch or marketing spike. Lower the cap if you want a hard stop; raise it only when you have budget headroom.',
  },
  {
    id: 'billing.alert_email',
    label: 'Usage alert email',
    summary:
      'Inbox that receives 50%, 80%, and 100% diagnosis quota alerts for this project.',
    howItWorks:
      'Stored on project_settings.alert_email. The usage-alerts cron sends at most one email per threshold per billing month. Leave blank to use the project owner email.',
    default: { value: 'project owner email' },
    backend: {
      table: 'project_settings',
      column: 'alert_email',
      endpoint: 'PUT /v1/admin/billing/alert-email',
      readBy: ['usage-alerts edge function'],
    },
    whenToChange:
      'Point at a shared ops inbox (e.g. billing@yourco.com) when the owner email is a personal Gmail.',
  },
  {
    id: 'billing.support_subject',
    label: 'Support subject',
    summary: 'One-line summary of your support request — appears as the email subject line.',
    howItWorks:
      'Submitted to the billing-support edge function which files a Zendesk-style ticket. Keep it specific (`"Refund for May overage — invoice 1234"`) so support doesn\'t bounce it back asking for clarification.',
    default: { value: 'empty' },
    backend: {
      table: 'support_requests',
      column: 'subject',
      endpoint: 'POST /v1/support/contact',
      readBy: ['billing-support edge function'],
    },
    whenToChange:
      'Always fill before submitting. The edge function rejects empty subjects with a 400.',
  },
  {
    id: 'billing.support_category',
    label: 'Support category',
    summary:
      'Category that routes your support ticket to the right team — billing, plan change, refund, technical, or other.',
    howItWorks:
      "The category is appended to the ticket body and used by the support inbox's automation to assign the right responder. Wrong category just means a slower first response, not a lost ticket.",
    default: { value: 'billing' },
    backend: {
      table: 'support_requests',
      column: 'category',
      endpoint: 'POST /v1/support/contact',
      readBy: ['billing-support edge function'],
    },
    whenToChange: 'Always pick the closest match. Use `other` only when nothing else fits.',
  },
  {
    id: 'billing.support_body',
    label: 'Support body',
    summary:
      'The full text of your support request — paste invoice numbers, screenshots, anything that helps the responder.',
    howItWorks:
      'Posted as the ticket body. Markdown is preserved on the support side, so feel free to use lists and code blocks. Maximum 10k chars.',
    default: { value: 'empty' },
    backend: {
      table: 'support_requests',
      column: 'body',
      endpoint: 'POST /v1/support/contact',
      readBy: ['billing-support edge function'],
    },
    whenToChange:
      "Include the invoice id and the dollar amount you're asking about — billing tickets without specifics get bounced.",
  },
];

const ONBOARDING: ConfigDoc[] = [
  {
    id: 'onboarding.project_name',
    label: 'Project name',
    summary:
      'Display name for your first project — visible in the active-project switcher and in routing-destination payloads.',
    howItWorks:
      'Used for display only. The internal `project_id` is generated and immutable; you can rename freely without breaking SDK keys or webhook subscriptions.',
    default: { value: 'unset' },
    backend: {
      table: 'projects',
      column: 'name',
      endpoint: 'POST /v1/admin/projects',
      readBy: ['admin UI', 'routing payloads'],
    },
    whenToChange:
      'Set during onboarding. Rename later as your product naming firms up — no migration needed.',
  },
  {
    id: 'onboarding.first_key_label',
    label: 'First API key label',
    summary: 'Human-readable label for the first API key — helps you find and revoke it later.',
    howItWorks:
      "Saved on the `project_api_keys` row alongside the hash and scopes. Pure metadata — the value isn't sent to the SDK, doesn't affect ingest behaviour.",
    default: { value: '"Default" if blank' },
    backend: {
      table: 'project_api_keys',
      column: 'label',
      endpoint: 'POST /v1/admin/projects/{id}/keys',
      readBy: ['admin UI', 'audit log'],
    },
    whenToChange:
      'Use a name that tells future-you what app or env this key belongs to — `"web-prod"`, `"native-staging"`, `"cursor-mcp-kenji"`.',
  },
];

const MCP: ConfigDoc[] = [
  {
    id: 'mcp.snippet_mode',
    label: 'Snippet mode',
    summary:
      'Picks which install snippet you copy — `mcp.json` (Claude Desktop / Cursor) vs `.env.local` (custom MCP host).',
    howItWorks:
      'Pure UI toggle. The first mode emits a `~/.cursor/mcp.json` block that registers the Mushi MCP server with stdio transport. The second emits an env-var preamble for hosts that read MCP config via env (some self-hosted Claude Desktop forks).',
    default: { value: 'mcp.json' },
    whenToChange:
      "Use `mcp.json` for off-the-shelf clients. Switch to `.env.local` only if your MCP host doesn't parse `mcp.json` natively.",
  },
];

const SDK_INSTALL: ConfigDoc[] = [
  {
    id: 'sdk-install.position',
    label: 'Widget position',
    summary:
      "Which corner of the user's app the bug-capture trigger pins to — top-left, top-right, bottom-left, bottom-right.",
    howItWorks:
      "Drives the live preview and the generated `Mushi.init({ widget: { position: '…' }})` call. The widget mounts in a shadow DOM, so the position is independent of the host app's CSS.",
    default: { value: 'bottom-right' },
    whenToChange:
      "Pick the corner that doesn't collide with your existing chrome (chat bubbles usually live bottom-right, so move Mushi to bottom-left if so).",
  },
  {
    id: 'sdk-install.trigger_mode',
    label: 'Trigger mode',
    summary:
      'Controls whether Mushi injects its own launcher, pins a slim header banner, attaches to your button, or stays programmatic-only.',
    howItWorks:
      'Five modes: `banner` (recommended) renders a slim 36 px strip pinned to the top or bottom of the viewport — less obtrusive than a FAB and never competes with bottom navigation. `auto` keeps the default editorial stamp button. `edge-tab` pins a vertical tab to the viewport edge. `attach` hides the default button and binds to `attachToSelector`. `manual` / `hidden` render no launcher — call `Mushi.open()` programmatically. The banner\'s body padding is adjusted automatically on mount and removed on dismiss, sdk.hide(), route suppression, or destroy().',
    default: { value: 'auto' },
    whenToChange:
      'Use `banner` for beta apps and user-research builds — maximises discoverability with minimal layout intrusion. Use `attach` for mature production apps with a help menu. Use `edge-tab` on tablet or when bottom nav competes. Use `manual` on regulated or fullscreen flows.',
    learnMore: {
      label: 'Trigger modes',
      href: 'https://kensaur.us/mushi-mushi/docs/concepts/trigger-modes',
    },
  },
  {
    id: 'sdk-install.banner_config',
    label: 'Banner launcher options',
    summary:
      'Customise the header-banner launcher: visual variant, position (top / bottom), announcement copy, and button labels.',
    howItWorks:
      'Only applies when `trigger_mode` is `banner`. `variant`: `neon` = electric-lime dev/beta aesthetic; `brand` = vermillion editorial feel; `subtle` = hairline muted strip. `position`: `top` pushes the page down; `bottom` lifts content up. `bannerMessage` + `bannerLabel` switch the strip to the rich admin-console layout (Beta pill + announcement line + flat text CTAs). `bugCta` / `featureCta` / `featureCtaLabel` let you relabel the buttons for your audience. Configure live in the console — the SDK picks up changes without re-init via the same runtime config pull that handles all other settings.',
    default: { value: 'variant: brand · position: top · featureCta: true' },
    backend: {
      table: 'project_settings',
      column: 'sdk_banner_variant / sdk_banner_position / sdk_banner_message / sdk_banner_label / sdk_banner_bug_cta / sdk_banner_feature_cta',
      endpoint: 'PATCH /v1/admin/settings',
      readBy: ['SDK runtime config pull (GET /v1/sdk/config)'],
    },
    whenToChange:
      'Use `subtle` when the banner must blend into a polished production UI. Use `neon` for internal beta tools where high visibility matters. Switch `position` to `bottom` when your app has a sticky top header that would collide with the banner.',
  },
  {
    id: 'sdk-install.smart_hide',
    label: 'Smart hide',
    summary:
      'Lets the launcher shrink, hide, or become an edge tab on mobile and while the user scrolls.',
    howItWorks:
      'The SDK listens for scroll and viewport changes inside the host app and adjusts only the launcher, not the capture pipeline. Reports can still be opened programmatically while the trigger is hidden or shrunk.',
    default: { value: 'off in 0.6; planned default after dogfood' },
    whenToChange:
      'Enable on consumer apps where the report button competes with bottom navigation, media controls, chat bubbles, or primary checkout CTAs.',
    learnMore: {
      label: 'Trigger modes',
      href: 'https://kensaur.us/mushi-mushi/docs/concepts/trigger-modes',
    },
  },
  {
    id: 'sdk-install.theme',
    label: 'Widget theme',
    summary: "Light, dark, or auto — auto follows the user's `prefers-color-scheme` media query.",
    howItWorks:
      'Auto re-evaluates on system theme change so the widget never goes white-on-white when the user toggles dark mode at night. Light/dark force the theme regardless of system preference.',
    default: { value: 'auto' },
    whenToChange:
      'Force `light` or `dark` when your app explicitly ignores system preference (rare). Otherwise stay on auto for the best a11y default.',
  },
  {
    id: 'sdk-install.trigger_text',
    label: 'Trigger text',
    summary:
      'Short label shown next to the bug-capture trigger button (or hidden if the button is icon-only).',
    howItWorks:
      'Localise this for non-English audiences (e.g. `バグ報告` for Japanese). Empty string = icon-only mode, which saves space but loses the affordance for first-time users.',
    default: { value: 'Report' },
    whenToChange:
      'Localise to your audience\'s language. Lengthen to "Report a bug" for novice users; shorten to "" for power users.',
  },
  {
    id: 'sdk-install.capture_console',
    label: 'Capture console',
    summary: 'Attaches the most recent console logs (configurable depth) to every bug report.',
    howItWorks:
      'A ring buffer wraps `console.log/warn/error` and keeps the last N entries. On report submit, the buffer is serialised and uploaded as part of the artifacts payload. No effect on production console behaviour — entries still print normally.',
    default: { value: 'on' },
    whenToChange:
      'Turn off in apps that log secrets to the console (rare and a smell). Keep on for everyday debugging.',
  },
  {
    id: 'sdk-install.capture_network',
    label: 'Capture network',
    summary: 'Attaches the most recent fetch/XHR requests + responses to every bug report.',
    howItWorks:
      'Hooks `fetch` and `XMLHttpRequest` to record method, URL, status, timing, and (optionally) bodies. Bodies are scrubbed of common secret patterns before upload, but the SDK errs on the side of NOT capturing bodies by default.',
    default: { value: 'on (headers/timing only)' },
    whenToChange:
      'Disable on apps with PII-laden API traffic (medical, financial). Enable bodies temporarily when investigating a hard-to-repro API bug.',
  },
  {
    id: 'sdk-install.capture_performance',
    label: 'Capture performance',
    summary:
      'Attaches Web Vitals (LCP, INP, CLS) and recent `PerformanceObserver` entries to bug reports.',
    howItWorks:
      'Subscribes to the standard `web-vitals` library hooks. Attached as a small JSON blob on the report — useful for "the page felt slow" reports the user can\'t describe more precisely.',
    default: { value: 'on' },
    whenToChange:
      'Keep on for consumer apps where perf matters. Turn off for internal tools where noise outweighs signal.',
  },
  {
    id: 'sdk-install.capture_element_picker',
    label: 'Capture element picker',
    summary:
      'Lets the user click an on-page element while filing a report; the CSS selector is attached.',
    howItWorks:
      'Adds a "Pick element" affordance to the in-widget form. On selection, the SDK computes a stable selector (id → data-test → class chain) and stamps it on the report payload.',
    default: { value: 'on' },
    whenToChange:
      'Turn off in apps where users describe bugs textually (forms, dashboards). Keep on for visually-rich apps where pointing > typing.',
  },
  {
    id: 'sdk-install.screenshot_mode',
    label: 'Screenshot mode',
    summary:
      'When the SDK captures a screenshot — when the report opens, always (continuous), or never.',
    howItWorks:
      '`on-report` is the default and respects user privacy. `auto` captures every few seconds (more diagnostic context, but storage-heavy). `off` disables screenshots entirely (compliance-friendly).',
    default: { value: 'on-report' },
    whenToChange:
      'Stay on `on-report` for most apps. Switch to `auto` for hard-to-repro intermittent bugs. Switch to `off` for HIPAA-style apps where any screenshot is a privacy risk.',
  },
  {
    id: 'sdk-install.screenshot_sensitive_hint',
    label: 'Screenshot privacy caption',
    summary:
      'A short "don\'t share sensitive information" caption shown under the screenshot preview before a reporter submits.',
    howItWorks:
      'When on, the widget shows the preview the reporter is about to send plus a privacy caption so they can redact or drop it first. Leave the custom box empty to use the SDK\'s localized default copy, type your own to override it, or turn the caption off entirely. Surfaces as `widget.screenshotSensitiveHint` in GET /v1/sdk/config and is honored by both the web and React Native widgets.',
    default: { value: 'on (default caption)' },
    backend: {
      table: 'project_settings',
      column: 'sdk_screenshot_sensitive_hint',
      endpoint: 'PUT /v1/admin/projects/:id/sdk-config',
      readBy: ['public route (GET /v1/sdk/config)', '@mushi-mushi/web widget', '@mushi-mushi/react-native widget'],
    },
    whenToChange:
      'Keep on for any app that captures screenshots — it is the reporter\'s chance to catch PII. Customize the copy to match your tone or compliance wording. Turn off only when screenshots are disabled or never contain user data.',
    learnMore: {
      label: 'Screenshot preview docs',
      href: 'https://github.com/kensaurus/mushi-mushi/blob/master/docs/SDK_SCREENSHOT_PREVIEW.md',
    },
  },
  {
    id: 'sdk-install.framework',
    label: 'Framework tab',
    summary:
      "Picks which framework's install snippet you copy — React, Vue, Svelte, React Native, Expo, Capacitor, or Vanilla.",
    howItWorks:
      'Pure UI toggle. Web frameworks (React/Vue/Svelte/Vanilla) wire up `Mushi.init()` from `@mushi-mushi/web` via the right adapter so framework error boundaries get hooked. Mobile frameworks (React Native / Expo) ship their own `<MushiProvider>` from `@mushi-mushi/react-native`. Capacitor uses the dedicated `@mushi-mushi/capacitor` plugin with `Mushi.configure(...)` and a follow-up `npx cap sync`.',
    default: { value: 'react' },
    whenToChange:
      'Pick whatever your app uses. Vanilla is the right answer for non-framework apps. For Capacitor → React Native migrations, see https://kensaur.us/mushi-mushi/docs/migrations/capacitor-to-react-native.',
  },
];

const ASSISTANT: ConfigDoc[] = [
  {
    id: 'assistant.config.enabled',
    label: 'Page-aware assistant',
    summary:
      'Adds an "Ask" tab to the SDK widget so users get answers about your app, grounded only in the page they\'re on and the knowledge you author.',
    howItWorks:
      'Turns on an Ask tab in the widget when enabled. Each question hits POST /v1/sdk/assistant, which grounds answers in the published page context plus your knowledge corpus — it never reads user rows, source, or env. Uses your project LLM key (Anthropic primary, OpenAI fallback) and logs every turn.',
    default: { value: 'false (off)' },
    backend: {
      table: 'project_settings',
      column: 'assistant_enabled',
      endpoint: 'PUT /v1/admin/projects/:id/assistant',
      readBy: ['sdk-assistant route (POST /v1/sdk/assistant)', 'public route (GET /v1/sdk/config)'],
    },
    whenToChange:
      'Turn on once you have written a knowledge corpus (Advanced). Leave off if you only want bug reporting — the widget works fully without it.',
    learnMore: { label: 'Assistant docs', href: 'https://github.com/kensaurus/mushi-mushi/blob/master/docs/SDK_ASSISTANT.md' },
  },
  {
    id: 'assistant.config.label',
    label: 'Tab label',
    summary: 'The label shown on the assistant tab in the widget (e.g. "Ask").',
    howItWorks:
      'Pure display string, capped at 24 chars. Returned in the SDK config so the widget can localise the tab without a rebuild.',
    default: { value: 'Ask' },
    backend: {
      table: 'project_settings',
      column: 'assistant_label',
      endpoint: 'PUT /v1/admin/projects/:id/assistant',
      readBy: ['public route (GET /v1/sdk/config)'],
    },
    whenToChange: 'Rename to match your product voice ("Help", "Guide", "Concierge").',
  },
  {
    id: 'assistant.config.greeting',
    label: 'Greeting',
    summary: 'First message shown on an empty assistant thread.',
    howItWorks:
      'Display-only, capped at 400 chars. Shown before the user types anything; it does not prime the LLM.',
    default: { value: 'Hi! Ask me anything about this page.' },
    backend: {
      table: 'project_settings',
      column: 'assistant_greeting',
      endpoint: 'PUT /v1/admin/projects/:id/assistant',
      readBy: ['public route (GET /v1/sdk/config)'],
    },
    whenToChange: 'Set expectations — tell users what the assistant can and cannot help with.',
  },
  {
    id: 'assistant.config.suggestions',
    label: 'Starter questions',
    summary: 'Up to 6 tappable starter-question chips shown on an empty thread.',
    howItWorks:
      'Stored as a JSON array of strings (each ≤ 120 chars, max 6). The widget renders them as one-tap prompts to lower the cold-start barrier.',
    default: { value: 'unset (no chips)' },
    backend: {
      table: 'project_settings',
      column: 'assistant_suggestions',
      endpoint: 'PUT /v1/admin/projects/:id/assistant',
      readBy: ['public route (GET /v1/sdk/config)'],
    },
    whenToChange: 'Seed with your top 3-6 FAQs so first-time users see what to ask.',
  },
  {
    id: 'assistant.config.knowledge',
    label: 'App knowledge corpus',
    summary:
      'Operator-authored text the assistant may cite — features, pricing, how-tos, FAQs. Capped at 40k chars and secret-scanned on save.',
    howItWorks:
      'This is the only grounding source besides the live page context. It is sent to the LLM on every turn, so it must never contain secrets — the save endpoint rejects text matching key/token/connection-string patterns (SECRET_DETECTED). Never returned in the public SDK config.',
    default: { value: 'unset', range: 'max 40,000 chars' },
    backend: {
      table: 'project_settings',
      column: 'assistant_knowledge',
      endpoint: 'PUT /v1/admin/projects/:id/assistant',
      readBy: ['sdk-assistant route (POST /v1/sdk/assistant)'],
    },
    whenToChange:
      'Expand it whenever users ask something the assistant could not answer. Review recent turns in Advanced → logs to find gaps. Never paste keys, tokens, or source.',
  },
];

/* ============================================================================
 *  REGISTRY
 * ============================================================================ */

/** Ordered groups — drives the section order in `docs/CONFIG_REFERENCE.md`. */
export const CONFIG_DOC_GROUPS: ReadonlyArray<{
  route: string;
  label: string;
  entries: readonly ConfigDoc[];
}> = [
  { route: 'settings.general', label: 'Settings → General', entries: SETTINGS_GENERAL },
  { route: 'settings.byok', label: 'Settings → BYOK (LLM keys)', entries: SETTINGS_BYOK },
  {
    route: 'settings.firecrawl',
    label: 'Settings → Firecrawl (web research)',
    entries: SETTINGS_FIRECRAWL,
  },
  { route: 'settings.devtools', label: 'Settings → Dev tools', entries: SETTINGS_DEVTOOLS },
  { route: 'projects', label: 'Projects', entries: PROJECTS },
  { route: 'integrations', label: 'Integrations', entries: INTEGRATIONS },
  { route: 'storage', label: 'Storage (BYO)', entries: STORAGE },
  { route: 'compliance', label: 'Compliance', entries: COMPLIANCE },
  { route: 'sso', label: 'SSO', entries: SSO },
  { route: 'prompt-lab', label: 'Prompt Lab', entries: PROMPT_LAB },
  { route: 'marketplace', label: 'Marketplace plugins', entries: MARKETPLACE },
  { route: 'anti-gaming', label: 'Anti-gaming', entries: ANTI_GAMING },
  { route: 'notifications', label: 'Notifications', entries: NOTIFICATIONS },
  { route: 'intelligence', label: 'Intelligence', entries: INTELLIGENCE },
  { route: 'billing', label: 'Billing', entries: BILLING },
  { route: 'onboarding', label: 'Onboarding', entries: ONBOARDING },
  { route: 'mcp', label: 'MCP install', entries: MCP },
  { route: 'sdk-install', label: 'SDK install card', entries: SDK_INSTALL },
  { route: 'assistant', label: 'Settings → Page-aware assistant', entries: ASSISTANT },
];

/** Flat lookup map. Built once at module load. Anything that takes a `helpId`
 *  resolves through `getConfigDoc(id)` so we never go back to scanning the
 *  arrays. */
const CONFIG_DOC_INDEX: Map<string, ConfigDoc> = (() => {
  const idx = new Map<string, ConfigDoc>();
  for (const group of CONFIG_DOC_GROUPS) {
    for (const entry of group.entries) {
      // Keep the first definition wins; the test suite enforces no duplicates
      // anyway, so this only matters during dev when someone is mid-rename.
      if (!idx.has(entry.id)) idx.set(entry.id, entry);
    }
  }
  return idx;
})();

/** Look up a config doc by its `helpId`. Returns `undefined` if no match —
 *  the `<ConfigHelp>` component renders nothing in that case and warns in
 *  dev. */
export function getConfigDoc(id: string): ConfigDoc | undefined {
  return CONFIG_DOC_INDEX.get(id);
}

/** Flat list of every entry, in declaration order. Used by tests and by the
 *  markdown generator. */
export const ALL_CONFIG_DOCS: readonly ConfigDoc[] = CONFIG_DOC_GROUPS.flatMap((g) => g.entries);
